import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertOctagon,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Clock,
  FileText,
  Fingerprint,
  Home,
  Lightbulb,
  Printer,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, claimPremium } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/premium-report/${id}`);
      setReport(data);
      setPaywall(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error("Could not load report");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [id]);

  const upgrade = async () => {
    setUpgrading(true);
    try {
      await claimPremium();
      toast.success("Premium unlocked!");
      await fetchReport();
    } catch {
      toast.error("Upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-grid">
        <Navbar />
        <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="mt-4 h-64 w-full" />
        </main>
      </div>
    );
  }

  if (paywall) {
    return (
      <div className="min-h-screen bg-grid">
        <Navbar />
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
          <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-10 text-center shadow-xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg">
              <Sparkles className="h-7 w-7" />
            </div>
            <h1 className="mt-6 font-heading text-3xl font-bold">Premium Report Required</h1>
            <p className="mt-3 text-slate-600">
              Unlock in-depth application guides, financial requirements, common rejection
              reasons, biometric guides, and more.
            </p>
            <Button
              onClick={upgrade}
              disabled={upgrading}
              data-testid="paywall-upgrade"
              className="mt-8 h-12 bg-gradient-to-br from-amber-500 to-orange-600 px-8 text-base font-semibold hover:from-amber-600 hover:to-orange-700"
            >
              {upgrading ? "Activating..." : "Claim Premium (Free demo)"}
            </Button>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 block w-full text-sm text-slate-500 hover:text-slate-900"
            >
              Go back
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!report) return null;

  const r = report.report;
  const lookup = report.lookup;

  return (
    <div className="min-h-screen bg-grid">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:py-12">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/results/${lookup.id}`)}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Back to summary
          </button>
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-report"
          >
            <Printer className="mr-2 h-4 w-4" /> Print / PDF
          </Button>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-lg print:shadow-none">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-600">
            <Sparkles className="h-3.5 w-3.5" /> Premium Report
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold sm:text-4xl">
            {lookup.nationality_name} → {lookup.destination_name}
          </h1>
          <div className="mt-1 text-sm capitalize text-slate-500">
            {lookup.purpose} · {lookup.duration_days} days
          </div>

          <section className="mt-8">
            <SectionTitle icon={FileText} title="Executive Summary" />
            <p className="whitespace-pre-wrap text-slate-700">{r.executive_summary}</p>
          </section>

          {r.detailed_document_checklist && (
            <section className="mt-10">
              <SectionTitle icon={CheckCircle2} title="Detailed Document Checklist" />
              <div className="space-y-3">
                {r.detailed_document_checklist.map((d, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="font-semibold text-slate-900">{d.item}</div>
                    {d.specifications && (
                      <div className="mt-1 text-sm text-slate-600">
                        <strong>Specs:</strong> {d.specifications}
                      </div>
                    )}
                    {d.where_to_get && (
                      <div className="mt-1 text-sm text-slate-500">
                        <strong>Where:</strong> {d.where_to_get}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {r.step_by_step_application && (
            <section className="mt-10">
              <SectionTitle icon={Clock} title="Step-by-Step Application" />
              <ol className="space-y-3">
                {r.step_by_step_application.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 text-slate-700">{s}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {r.financial_requirements && (
            <section className="mt-10">
              <SectionTitle icon={Wallet} title="Financial Requirements" />
              <p className="text-slate-700">{r.financial_requirements.explanation}</p>
              {r.financial_requirements.min_funds_usd && (
                <div className="mt-3 inline-block rounded-lg bg-teal-50 px-4 py-2 font-mono text-sm text-teal-700">
                  Min funds: ${r.financial_requirements.min_funds_usd}
                </div>
              )}
              {r.financial_requirements.proof_types_accepted && (
                <ul className="mt-3 space-y-1 text-sm text-slate-600">
                  {r.financial_requirements.proof_types_accepted.map((p, i) => (
                    <li key={i}>• {p}</li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {r.insurance_requirements && (
            <section className="mt-10">
              <SectionTitle icon={ShieldCheck} title="Travel Insurance" />
              <p className="text-slate-700">{r.insurance_requirements.notes}</p>
              {r.insurance_requirements.min_coverage_usd && (
                <div className="mt-3 inline-block rounded-lg bg-emerald-50 px-4 py-2 font-mono text-sm text-emerald-700">
                  Min coverage: ${r.insurance_requirements.min_coverage_usd}
                </div>
              )}
            </section>
          )}

          {r.photo_specifications && (
            <section className="mt-10">
              <SectionTitle icon={Camera} title="Photo Specifications" />
              <p className="text-slate-700">{r.photo_specifications}</p>
            </section>
          )}

          {r.biometric_appointment_guide && (
            <section className="mt-10">
              <SectionTitle icon={Fingerprint} title="Biometric Appointment" />
              <p className="text-slate-700">{r.biometric_appointment_guide}</p>
            </section>
          )}

          {r.accommodation_proof && (
            <section className="mt-10">
              <SectionTitle icon={Home} title="Accommodation Proof" />
              <p className="text-slate-700">{r.accommodation_proof}</p>
            </section>
          )}

          {r.common_rejection_reasons && (
            <section className="mt-10">
              <SectionTitle icon={AlertOctagon} title="Common Rejection Reasons" />
              <ul className="space-y-2">
                {r.common_rejection_reasons.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                    {c}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {r.premium_tips && (
            <section className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <SectionTitle icon={Lightbulb} title="Insider Tips" />
              <ul className="space-y-2">
                {r.premium_tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    {t}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-teal-600" />
      <h2 className="font-heading text-lg font-semibold">{title}</h2>
    </div>
  );
}
